import { BitmapTextFieldProperties } from "@lib/titan2/dist/game/control/properties/BitmapTextFieldProperties";
import { SpriteProperties } from "@lib/titan2/dist/game/control/properties/SpriteProperties";
import { TextFieldProperties } from "@lib/titan2/dist/game/control/properties/TextFieldProperties";
import { TitanBitmapTextField } from "@lib/titan2/dist/game/control/TitanBitmapTextField";
import { TitanSpine } from "@lib/titan2/dist/game/control/TitanSpine";
import { TitanSprite } from "@lib/titan2/dist/game/control/TitanSprite";
import { ArrayUtil } from "@lib/titan2/dist/util/collection/ArrayUtil";
import { Logger } from "@lib/titan2/dist/util/log/Logger";
import { DestroyUtil } from "@lib/titan2/dist/util/view/DestroyUtil";
import { GraphicsUtil } from "@lib/titan2/dist/util/view/GraphicsUtil";
import { SpineUtil } from "@lib/titan2/dist/util/view/SpineUtil";
import { ViewUtils } from "@lib/titan2/dist/util/view/ViewUtils";
import { gsap } from "gsap";
import { MotionBlurFilter } from "pixi-filters";
import { ReelEvent, SlingoEvent } from "../../../controller/event/SlingoEvent";
import { TweenConstants } from "../constant/TweenConstants";
import { SlingoStyle } from "../style/SlingoStyle";
import { AbstractGameView } from "./AbstractGameView";
import { ReelSpinSymbol } from "./ReelSpinSymbol";
import { ReelSymbol } from "./ReelSymbol";
/**
 * Reel.ts
 */
export class Reel extends AbstractGameView {
    static resetIndex() {
        Reel.index = 0;
    }
    destroy() {
        this.uiProperties = null;
        this.landscapeProperties = null;
        this.portraitProperties = null;
        DestroyUtil.destroy(this.background);
        this.background = null;
        DestroyUtil.destroy(this.backgroundPurchase);
        this.backgroundPurchase = null;
        this.symbolAnimation = null;
        this.symbolAnimations && this.symbolAnimations.getValues().forEach(value => value.destroy());
        this.symbolAnimations = null;
        this.symbolHolder = null;
        DestroyUtil.destroy(this.symbol);
        this.symbol = null;
        DestroyUtil.destroy(this.disabled);
        this.disabled = null;
        DestroyUtil.destroy(this.disabledFilter);
        this.disabledFilter = null;
        this.freeSpin = null;
        DestroyUtil.destroy(this.backgroundAnimation);
        this.backgroundAnimation = null;
        DestroyUtil.destroy(this.highlightAnimation);
        this.highlightAnimation = null;
        this.reelMask.destroy();
        this.reelMask = null;
        this.reelSetSymbolsStandard.forEach(symbol => symbol.destroy());
        this.reelSetSymbolsStandard = null;
        this.reelSetSymbolsPurchase.forEach(symbol => symbol.destroy());
        this.reelSetSymbolsPurchase = null;
        this.reelSetSymbolHolder = null;
        this.reelSetAnimation = null;
        this.currentReelSet = null;
        this.stopId = null;
        this.stopDispatched = null;
        this.filter = null;
        this.container = null;
        DestroyUtil.destroy(this.suspenseAnimation);
        this.suspenseAnimation = null;
        super.destroy();
    }
    get hasBackground() {
        return this.uiProperties?.backgroundProperties?.id?.length > 0;
    }
    get hasBackgroundPurchase() {
        return this.uiProperties.backgroundPurchaseProperties && !this.uiProperties.backgroundProperties.equals(this.uiProperties.backgroundPurchaseProperties);
    }
    constructor(factory, properties, portraitProperties) {
        super(factory, properties, portraitProperties);
    }
    hideBackground() {
        this.background && (this.background.renderable = false);
        this.backgroundPurchase && (this.backgroundPurchase.renderable = false);
    }
    // TitanPanel arrange functions use the Reel's width and height, which can be altered by the presence of a rendering symbol animation.
    getWidth() {
        return this.uiProperties.size.x;
    }
    getHeight() {
        return this.uiProperties.size.y;
    }
    createProperties(properties, portraitProperties) {
        super.createProperties(properties, portraitProperties);
        this.landscapeProperties = this.landscapeProperties;
        this.portraitProperties = this.portraitProperties;
        this.uiProperties = this.uiProperties;
    }
    create() {
        Reel.index = Reel.index || 0;
        this.index = Reel.index;
        Reel.index++;
        super.create();
        this.container = ViewUtils.createContainer(this, "Reel");
        this.symbolHolder = ViewUtils.createContainer(this, "symbolHolder");
        this.createBackgroundStatic();
        this.createReelAnimation();
        if (this.uiProperties.symbolProperties instanceof BitmapTextFieldProperties) {
            this.symbol = new TitanBitmapTextField(this.landscapeProperties.symbolProperties, this.portraitProperties?.symbolProperties);
            this.symbolHolder.addChild(this.symbol);
        }
        else if (this.uiProperties.symbolProperties instanceof TextFieldProperties) {
            this.symbol = ViewUtils.createField(this.symbolHolder, this.landscapeProperties.symbolProperties, this.portraitProperties?.symbolProperties);
        }
        this.createDisabled();
        this.createSymbolAnimations();
        this.createBackgroundAnimation();
        this.createHighlightAnimation();
        this.createMask();
        this.createSuspenseAnimation();
    }
    createBackgroundStatic() {
        if (this.hasBackground) {
            this.background = TitanSprite.createSprite(this, "background", this.landscapeProperties.backgroundProperties, this.portraitProperties?.backgroundProperties);
        }
        if (this.hasBackgroundPurchase) {
            this.backgroundPurchase = TitanSprite.createSprite(this, "backgroundPurchase", this.landscapeProperties.backgroundPurchaseProperties, this.portraitProperties?.backgroundPurchaseProperties);
            this.backgroundPurchase.renderable = false;
        }
    }
    createReelAnimation() {
        this.filter = new MotionBlurFilter(this.uiProperties.motionBlur);
        this.reelSetSymbolHolder = ViewUtils.createContainer(this, "reelSetSymbolHolder");
        this.container.addChild(this.reelSetSymbolHolder);
        this.reelSetSymbolsStandard = this.createReelSymbols(this.uiProperties.reelSetsStandard[this.index]);
        this.reelSetSymbolsPurchase = this.createReelSymbols(this.uiProperties.reelSetsPurchase[this.index]);
    }
    createReelSymbols(reelSet) {
        return reelSet.map(id => {
            // Don't remake a symbol if it's already part of reelSetSymbolsStandard0
            const existingSymbol = this.getReelSymbols(id);
            if (existingSymbol) {
                return existingSymbol;
            }
            let item;
            if (isNaN(id)) {
                item = TitanSprite.createSprite(this.reelSetSymbolHolder, "item", new SpriteProperties(SlingoStyle.REEL_SYMBOL + id, SlingoStyle.MODULE), undefined);
            }
            else {
                if (this.landscapeProperties.symbolProperties instanceof BitmapTextFieldProperties) {
                    item = new TitanBitmapTextField(this.landscapeProperties.symbolProperties);
                    this.reelSetSymbolHolder.addChild(item);
                }
                else {
                    item = ViewUtils.createField(this.reelSetSymbolHolder, this.landscapeProperties.symbolProperties);
                }
                item.setText(id);
            }
            return new ReelSpinSymbol(id, item);
        });
    }
    getReelSymbols(reelSet) {
        const findInReelSet = (reelSet) => {
            return reel => reelSet?.find(rs => rs.id === reel);
        };
        const findInStandardReelSet = (reel) => {
            return findInReelSet(this.reelSetSymbolsStandard)(reel);
        };
        if (Array.isArray(reelSet)) {
            return reelSet.map(reel => findInStandardReelSet(reel));
        }
        else {
            findInStandardReelSet(reelSet);
        }
    }
    createMask() {
        this.reelMask = GraphicsUtil.createMask(this.uiProperties.size);
        this.addMask();
    }
    addMask() {
        this.container.addChild(this.reelMask);
        this.container.mask = this.reelMask;
    }
    removeMask() {
        this.container.removeChild(this.reelMask);
        this.container.mask = null;
    }
    createSuspenseAnimation() {
        if (this.landscapeProperties.suspenseAnimationProperties) {
            this.suspenseAnimation = new TitanSpine(this.landscapeProperties.suspenseAnimationProperties, this.portraitProperties?.suspenseAnimationProperties);
        }
    }
    createSymbolAnimations() {
        this.symbolAnimation = new ReelSymbol(this.landscapeProperties.symbolAnimationSpineProperties, this.portraitProperties?.symbolAnimationSpineProperties, this.uiProperties);
    }
    createDisabled() {
        if (this.landscapeProperties.disabledProperties) {
            this.disabled = TitanSprite.createSprite(this, "disabled", this.landscapeProperties.disabledProperties, this.portraitProperties?.disabledProperties);
            this.disabled.alpha = 0.5;
            this.removeChild(this.disabled);
        }
        else if (this.landscapeProperties.disabledFilterProperties) {
            this.disabledFilter = this.landscapeProperties.disabledFilterProperties.createFilter();
        }
    }
    createBackgroundAnimation() {
        if (this.landscapeProperties.backgroundAnimationProperties) {
            this.backgroundAnimation = new TitanSpine(this.landscapeProperties.backgroundAnimationProperties, this.portraitProperties?.backgroundAnimationProperties);
        }
    }
    createHighlightAnimation() {
        if (this.landscapeProperties.highlightAnimationProperties) {
            this.highlightAnimation = new TitanSpine(this.landscapeProperties.highlightAnimationProperties, this.portraitProperties?.highlightAnimationProperties);
        }
    }
    reset() {
        this.enable();
        this.removeAll();
    }
    removeAll() {
        this.removeHighlight();
        this.removeBackgroundAnimation();
        this.removeSymbol();
        this.removeSymbolAnimation();
        this.removeReelAnimation();
        this.removeSuspenseAnimation();
        this.removeMask();
    }
    play() {
        this.reset();
        this.addMask();
        this.addReelAnimation();
    }
    hold() {
        this.removeDisabled();
    }
    stop(id) {
        if (this.reelSetAnimation) {
            this.stopId = id;
            this.reelSetAnimation.kill();
        }
        this.removeAll();
        this.addSymbol(id);
        Logger.debug(`<============ REEL ${this.index} STOPPED ============>`); //`
        this.dispatchStoppedOnce();
    }
    dispatchStoppedOnce() {
        if (!this.stopDispatched) {
            this.stopDispatched = true;
            this.dispatchGame(ReelEvent.REEL_STOPPED);
        }
    }
    stopEmpty() {
        this.removeReelAnimation();
    }
    enable() {
        this.removeDisabled();
    }
    disable() {
        this.addDisabled();
    }
    highlight() {
        this.addHighlight();
    }
    unhighlight() {
        this.removeHighlight();
    }
    updateBackground(purchaseSpins = false) {
        if (purchaseSpins && this.hasBackgroundPurchase) {
            this.background.renderable = false;
            this.backgroundPurchase.renderable = true;
        }
        else {
            this.background && (this.background.renderable = true);
            this.backgroundPurchase && (this.backgroundPurchase.renderable = false);
        }
    }
    columnFullyDabbed() {
        const cellsRemaining = this.model.getTicket().getRemainingCells();
        return cellsRemaining.findIndex((cell) => cell.getColumn() === this.index) === -1;
    }
    addSymbol(id) {
        switch (id) {
            case SlingoStyle.JOKER:
                this.symbolHolder.addChild(this.symbolAnimation);
                if (this.columnFullyDabbed()) {
                    this.symbolAnimation.gotoAndStop();
                    this.disable();
                }
                else {
                    this.symbolAnimation.strikeJoker();
                }
                break;
            case SlingoStyle.SUPER_JOKER:
                this.symbolHolder.addChild(this.symbolAnimation);
                this.symbolAnimation.strikeSuperJoker();
                break;
            case SlingoStyle.FREE_SPIN:
                this.symbolHolder.addChild(this.symbolAnimation);
                this.symbolAnimation.strikeFreeSpin();
                break;
            default:
                this.symbol.setText(id);
                if (this.idMatchesTicket(id)) {
                    this.symbolHolder.addChild(this.symbolAnimation);
                    this.symbolAnimation.strikeNumber();
                }
                this.symbol.visible = true;
        }
        this.currentId = id;
        //add a little bump when it stops
        gsap.to(this.symbolHolder, { y: "+=5", yoyo: true, repeat: 1, duration: 0.05 });
    }
    addReelAnimation() {
        this.stopDispatched = false;
        this.removeChild(this.symbolHolder);
        if (this.model.isPurchaseSpin()) {
            this.currentReelSet = this.reelSetSymbolsPurchase.slice();
        }
        else {
            this.currentReelSet = this.reelSetSymbolsStandard.slice();
        }
        this.reelSetSymbolHolder.children.forEach(child => (child.visible = false));
        const displaySymbols = this.model.getReelDisplayData().getDisplaySymbols();
        ArrayUtil.shuffle(this.currentReelSet, true);
        if (displaySymbols) {
            const currentSymbol = displaySymbols[this.index];
            const currentSymbolIndex = this.currentReelSet.findIndex(elem => elem.id === currentSymbol?.getId());
            if (currentSymbolIndex >= 0) {
                [this.currentReelSet[0], this.currentReelSet[currentSymbolIndex]] = [this.currentReelSet[currentSymbolIndex], this.currentReelSet[0]]; // Move currently displayed symbol to the front of the list
            }
        }
        const sizeY = this.uiProperties.size.y;
        for (let i = 0; i < this.currentReelSet.length; i++) {
            this.currentReelSet[i].y = this.currentReelSet[i].getBaseY() - sizeY * i;
            this.currentReelSet[i].getSymbol().visible = true;
        }
        const startingPositions = this.currentReelSet.map(elem => elem.y);
        const offset = { y: -sizeY };
        const setHeight = sizeY * this.currentReelSet.length;
        this.stopId = null;
        this.reelSetAnimation = gsap.to(offset, {
            y: sizeY * (this.currentReelSet.length - 1),
            duration: TweenConstants.REEL_DURATION,
            repeat: -1,
            ease: "none",
            onUpdate: () => {
                this.currentReelSet.forEach((elem, i) => {
                    elem.y = startingPositions[i] + ((offset.y + sizeY) % setHeight);
                    if (elem.y > sizeY) {
                        elem.y -= setHeight;
                    }
                });
            },
        });
        this.reelSetSymbolHolder.filters = [this.filter];
        this.container.addChild(this.reelSetSymbolHolder);
    }
    addDisabled() {
        var _a;
        if (this.symbolAnimation) {
            switch (this.currentId) {
                case SlingoStyle.JOKER:
                    this.symbolAnimation.stopJoker();
                    break;
                case SlingoStyle.SUPER_JOKER:
                    this.symbolAnimation.stopSuperJoker();
                    break;
                case SlingoStyle.FREE_SPIN:
                    this.symbolAnimation.stopFreeSpin();
                    break;
                default:
                    this.symbolAnimation.gotoAndStop();
            }
        }
        if (this.disabled) {
            this.addChild(this.disabled);
        }
        else if (this.disabledFilter) {
            (_a = this.symbolHolder).filters ?? (_a.filters = []);
            // Don't add another filter if it'salready applied
            if (!this.symbolHolder.filters?.some(f => f === this.disabledFilter)) {
                this.symbolHolder.filters.push(this.disabledFilter);
            }
        }
    }
    removeDisabled() {
        if (this.disabled) {
            this.removeChild(this.disabled);
        }
        else if (this.disabledFilter) {
            const disabledIndex = this.symbolHolder.filters?.indexOf(this.disabledFilter);
            if (disabledIndex >= 0) {
                this.symbolHolder.filters?.splice(disabledIndex, 1);
            }
        }
    }
    addHighlight() {
        this.highlightAnimation.play();
        this.addChild(this.highlightAnimation);
        this.dispatchGame(SlingoEvent.REEL_SYMBOL_HIGHLIGHT);
    }
    addBackgroundAnimation() {
        this.background.alpha = 0;
        this.backgroundAnimation.play();
        this.addChildAt(this.backgroundAnimation, this.getChildIndex(this.background));
    }
    removeHighlight() {
        this.removeChild(this.highlightAnimation);
    }
    removeBackgroundAnimation() {
        this.removeChild(this.backgroundAnimation);
        this.background && (this.background.alpha = 1);
    }
    removeReelAnimation() {
        this.container.removeChild(this.reelSetSymbolHolder);
        this.container.addChild(this.symbolHolder);
    }
    removeSuspenseAnimation() {
        if (this.suspenseAnimation) {
            this.removeChild(this.suspenseAnimation);
        }
    }
    suspense() {
        this.dispatchGame(SlingoEvent.SUSPENSE_SPIN);
        if (this.suspenseAnimation) {
            this.addChild(this.suspenseAnimation);
            SpineUtil.playLoop(this.suspenseAnimation);
        }
        if (this.reelSetAnimation) {
            this.reelSetAnimation.timeScale(2.0);
        }
    }
    removeSymbol() {
        this.symbol.visible = false;
    }
    removeSymbolAnimation() {
        if (this.symbolAnimation) {
            this.symbolHolder.removeChild(this.symbolAnimation);
        }
    }
    getSymbolAnimation(id) {
        let symbol;
        if (this.symbolAnimations?.containsKey(id)) {
            symbol = this.symbolAnimations.getValue(id);
        }
        return symbol;
    }
    idMatchesTicket(id) {
        const ticketDab = this.model.getDabData();
        const autoHighlightCells = ticketDab.getAutoHighlightCells();
        return autoHighlightCells.findIndex(cell => cell.getNumber() === parseInt(id)) > -1;
    }
}
//# sourceMappingURL=Reel.js.map